/**
 * Click Analytics Tracker for OpticWave
 *
 * This script tracks user clicks on important elements and sends them to Firebase
 * Designed to be lightweight and non-intrusive to the user experience
 */

import {Firestore, arrayUnion, doc, updateDoc} from 'firebase/firestore';
import {logger} from '../logger';

// Holds the click data until it's ready to be sent to Firebase
let pendingClicks: ClickData[] = [];
let isClickTrackingEnabled = true;
let userSession: string | null = null;
let dbInstance: Firestore | null = null;

interface ClickData {
  element: string;
  timestamp: Date;
  pathname: string;
  userAgent?: string;
  [key: string]: any;
}

interface ExerciseSettings {
  [key: string]: any;
}

interface TrackingAPI {
  disable: () => void;
  enable: () => void;
  trackClick: (element: string, metadata?: Record<string, any>) => void;
  trackExerciseSettings: (exerciseType: string, settings: ExerciseSettings) => void;
}

// Initialize the click tracker
export function initializeClickTracker(db: Firestore, sessionId: string): TrackingAPI {
  if (!db) {
    logger.error('Firebase Firestore instance not provided to click tracker');
    throw new Error('Firestore instance required for click tracker');
  }

  // Store references
  dbInstance = db;
  userSession = sessionId;

  // Clear any existing data
  pendingClicks = [];
  isClickTrackingEnabled = true;

  // Start click listener
  startClickTracking();

  // Set up interval to periodically send clicks (every 60 seconds)
  const intervalId = setInterval(() => {
    if (pendingClicks.length > 0) {
      saveClicksToFirebase();
    }
  }, 60000);

  // Save clicks when user leaves page
  window.addEventListener('beforeunload', () => {
    if (pendingClicks.length > 0) {
      saveClicksToFirebase(true);
    }
    clearInterval(intervalId);
  });

  logger.info('Click tracker initialized for session:', sessionId);

  return {
    disable: () => {
      isClickTrackingEnabled = false;
    },
    enable: () => {
      isClickTrackingEnabled = true;
    },
    trackClick: (element: string, metadata?: Record<string, any>) => trackClickManually(element, metadata),
    trackExerciseSettings: (exerciseType: string, settings: ExerciseSettings) =>
      trackExerciseSettings(exerciseType, settings),
  };
}

// Start tracking clicks across the application
function startClickTracking(): void {
  document.addEventListener('click', (event: MouseEvent) => {
    if (!isClickTrackingEnabled) return;

    // Get the clicked element
    const element = event.target as HTMLElement;

    // Try to get a meaningful identifier for the element
    let elementIdentifier = getElementIdentifier(element);

    // Track the click if we have a meaningful identifier
    if (elementIdentifier) {
      trackClick(elementIdentifier);
    }
  });
}

// Get a meaningful identifier for the clicked element
function getElementIdentifier(element: HTMLElement): string | null {
  // If the element has an ID, use that
  if (element.id) {
    return `#${element.id}`;
  }

  // If the element has a data-testid attribute, use that
  if (element.dataset && element.dataset.testid) {
    return `[data-testid="${element.dataset.testid}"]`;
  }

  // If the element has a role attribute, use that with its text content
  if (element.getAttribute('role')) {
    const text = element.textContent?.trim().substring(0, 20);
    return `${element.tagName.toLowerCase()}[role="${element.getAttribute('role')}"]${text ? `: "${text}"` : ''}`;
  }

  // For buttons, use the text content
  if (element.tagName === 'BUTTON') {
    const text = element.textContent?.trim().substring(0, 20);
    return `button: "${text || 'unnamed'}"`;
  }

  // For links, use the text content or href
  if (element.tagName === 'A') {
    const text = element.textContent?.trim().substring(0, 20);
    return `link: "${text || element.getAttribute('href') || 'unnamed'}"`;
  }

  // Check if it's an icon or image inside a button or link
  const parentButton = element.closest('button');
  if (parentButton) {
    const text = parentButton.textContent?.trim().substring(0, 20);
    return `button-child: "${text || 'unnamed'}"`;
  }

  const parentLink = element.closest('a');
  if (parentLink) {
    const text = parentLink.textContent?.trim().substring(0, 20);
    return `link-child: "${text || parentLink.getAttribute('href') || 'unnamed'}"`;
  }

  // For specific UI elements like checkboxes, radio buttons, etc.
  if (element.tagName === 'INPUT') {
    const inputType = element.getAttribute('type');
    const inputName = element.getAttribute('name');
    return `input[type="${inputType}"${inputName ? ` name="${inputName}"` : ''}]`;
  }

  // Skip elements without meaningful identifiers
  return null;
}

// Record a click
function trackClick(elementIdentifier: string): void {
  const click: ClickData = {
    element: elementIdentifier,
    timestamp: new Date(),
    pathname: window.location.pathname,
    userAgent: navigator.userAgent,
  };

  // Add to pending clicks
  pendingClicks.push(click);

  // If we have 10 or more clicks, save them to firebase
  if (pendingClicks.length >= 10) {
    saveClicksToFirebase();
  }

  logger.debug('Tracked click on:', elementIdentifier);
}

// Manually track a click (can be called from app code)
function trackClickManually(element: string, metadata: Record<string, any> = {}): void {
  if (!isClickTrackingEnabled) return;

  const click: ClickData = {
    element,
    timestamp: new Date(),
    pathname: window.location.pathname,
    ...metadata,
  };

  pendingClicks.push(click);
  logger.debug('Manually tracked click on:', element);
}

// Save pending clicks to Firebase
function saveClicksToFirebase(isSynchronous: boolean = false): void {
  if (!userSession || !dbInstance) {
    logger.warn('Cannot save clicks: No active session ID or database instance');
    return;
  }

  // Clone and clear pending clicks
  const clicksToSave = [...pendingClicks];
  pendingClicks = [];

  // Get the session document reference
  const sessionRef = doc(dbInstance, 'sessions', userSession);

  try {
    const updateOperation = updateDoc(sessionRef, {
      lastClickedElement: clicksToSave[clicksToSave.length - 1].element,
      clicks: arrayUnion(...clicksToSave),
      lastUpdated: new Date(),
    });

    if (isSynchronous) {
      // For beforeunload events, just log that we're exiting
      logger.info('Page unloading, analytics data may not be saved');
    } else {
      updateOperation
        .then(() => logger.debug('Saved clicks to Firebase'))
        .catch(err => {
          // Just log the error without throwing exceptions
          logger.error('Error saving clicks:', err);
          // Don't rethrow to prevent app from crashing
        });
    }
  } catch (error) {
    // Just log the error without affecting app functionality
    logger.error('Error preparing click data for Firebase:', error);
  }
}

// Track exercise settings selections
function trackExerciseSettings(exerciseType: string, settings: ExerciseSettings): void {
  if (!userSession || !dbInstance) {
    logger.warn('Cannot save exercise settings: No active session ID or database instance');
    return;
  }

  const timestamp = new Date();

  // Get the session document reference
  const sessionRef = doc(dbInstance, 'sessions', userSession);

  // Create an exercise entry with settings
  const exerciseData = {
    type: exerciseType,
    settings: settings,
    timestamp: timestamp,
  };

  try {
    // Add to the exercises array in the session document
    updateDoc(sessionRef, {
      exercises: arrayUnion(exerciseData),
      lastUpdated: timestamp,
    })
      .then(() => logger.debug('Saved exercise settings to Firebase'))
      .catch(err => {
        // Just log the error without throwing exceptions
        logger.error('Error saving exercise settings:', err);
        // Don't rethrow to prevent app from crashing
      });

    // Also track as a click for consistency
    trackClickManually(`exercise-settings:${exerciseType}`, {
      settings,
      pathname: window.location.pathname,
    });
  } catch (error) {
    // Just log errors without breaking the app flow
    logger.error('Error tracking exercise settings update:', error);
  }
}
